import { FormDataStructure, FormManager } from '@visto-tech/forms';
import { getFakeAnswerForQuestion } from 'components/DynamicApiForm/DynamicApiFormFaker';
import Form from 'dataLayer/Form';
import Question from 'dataLayer/Question';
import { useActiveUser } from 'hooks/useActiveUser';
import { first } from 'lodash';
import { observer } from 'mobx-react-lite';
import { FC } from 'react';
import toast from 'react-hot-toast';
import { removeSuffix } from 'utils/helpers';

/**
 * A "Fake It" button for a single repeater entry (the Add/Edit Item modal),
 * only rendered for Visto users. The RepeaterTable renderer is called once per
 * question of the entry, so this only renders itself for the entry's first
 * question, to keep a single button at the top of the modal.
 */
export const RepeaterEntryFaker: FC<{
  formManager:
    | FormManager<FormDataStructure<string | number | symbol>>
    | undefined;
  name: string;
  repeaterForm?: Form;
}> = observer(({ formManager, name, repeaterForm }) => {
  const user = useActiveUser();

  if (!user?.isVistoUser() || !formManager || !repeaterForm) {
    return null;
  }

  const questions = repeaterForm.content as Question[];
  const interval = first(name.match(/[^-]*$/));

  const firstQuestion = questions.find(
    (question) => `${question.label}-${interval}` in formManager.formData
  );

  if (removeSuffix(name) !== firstQuestion?.label) {
    return null;
  }

  const handleFillInFakerData = (e: any) => {
    e.preventDefault();

    questions.forEach((question) => {
      const questionLabel = `${question.label}-${interval}`;

      const skipQuestion =
        !(questionLabel in formManager.formData) ||
        formManager.formData[questionLabel] !== '' ||
        !question.optimisticallyMeetsAllConditions(
          formManager,
          interval,
          questions
        );

      if (skipQuestion) {
        return;
      }

      const value = getFakeAnswerForQuestion(question);

      if (value === null) {
        return;
      }

      formManager.setFormData(questionLabel, value);
    });

    toast.success('Faker data filled');
  };

  return (
    <div className="flex justify-center mb-4">
      <button
        className="repeater-fake-it font-bold fs12 underline"
        onClick={handleFillInFakerData}
      >
        Fake It
      </button>
    </div>
  );
});
